import IOS from "app/core/components/devices/IOS"
import { IDeviceOptions } from "app/paywalls/helpers/types"
import { TPageStyles } from "@superwall-me/schema"
import { TPaywallTemplate } from "app/schema/paywallTemplate/paywallTemplate"
import { Sub } from "@superwall-me/schema"
import { Dispatch, FC, HTMLProps, RefObject, SetStateAction, useMemo, useState } from "react"
import { IPaywallVariables, usePaywallPreviewLifecycle } from "../hooks/usePaywallPreviewLifecycle"
import { ParsedPaywall } from "@superwall/db/paywall"
import { SOverlaySpinner } from "@superwall/legacy-ui"
import tw from "tailwind-styled-components"
import { SFlex } from "@superwall/legacy-ui"
import { Icon } from "@superwall/legacy-ui"

const Container = tw.div`
  flex
  flex-col
  justify-center
  items-center
`

const OrientationIconContainer = tw.div`
  cursor-pointer
`

const LoaderContainer = tw.div<{ $isHidden: boolean }>`
  absolute
  top-0
  right-0
  bottom-0
  left-0
  z-10
  transition
  duration-250
  ease-out

  ${({ $isHidden }) => ($isHidden ? "opacity-0 pointer-events-none" : "")}
`

export interface IPaywallPreview
  extends
    Pick<HTMLProps<HTMLDivElement>, "className" | "style">,
    IPaywallVariables,
    IDeviceOptions {
  /**
   * iframe src url
   */
  previewUrl: string
  /**
   * background color the paywall preview
   * @default black
   */
  backgroundColor?: string
  /**
   * border color of the iframe
   * @default "#44494E"
   */
  deviceColor?: string
  /**
   * scale of the paywall preview
   * @default 1.0
   */
  scale?: number
  /**
   * background color the paywall preview
   */
  maxWidth?: number
  /**
   * changes the preview to either show the modal or not
   * @deafult false
   */
  isModal?: boolean
  /**
   * forces a reload of the iframe
   * https://stackoverflow.com/questions/48830316/how-do-you-reload-an-iframe-with-reactjs
   */
  iFrameRenderKey?: string
  /**
   * controlls if the paywall preview should stick to top of parent in
   * during scroll
   * @default false
   */
  sticky?: boolean
  /**
   * called when paywall.js becomes ready inside the iFrame
   */
  setPaywallSubstitutions?: Dispatch<SetStateAction<Sub[] | undefined>>
  setSubstitutionsFromPing?: Dispatch<SetStateAction<Sub[] | undefined>>
  /**
   * Enable hover & clicks on the paywall previewer
   */
  enableHoverAndClick?: boolean
  onChangePageStyles?: (val: TPageStyles) => void

  skipBorder?: boolean
  setTemplateJson?: Dispatch<SetStateAction<TPaywallTemplate | null>>
  paywall: ParsedPaywall | undefined
  hasError?: boolean
  setIframeRef?: (ref: RefObject<HTMLIFrameElement | null>) => void
  isSubstitutionFocused?: boolean
  selectedSubstitutionKey?: string
  setSelectedSubstitutionKey?: Dispatch<SetStateAction<string | undefined>>
  hoverSubstitutionKey?: string | null
  isLoading?: boolean
  mode?: "editorV3" | "editorV2"
}

const PaywallPreview: FC<IPaywallPreview> = (props) => {
  const [orientation, setOrientation] = useState<"portrait" | "landscape">("portrait")

  const {
    previewUrl,
    backgroundColor = "black",
    deviceColor = "#44494E",
    scale = 1.0,
    maxWidth,
    contentWidth = 375,
    aspectRatio = 19.5 / 9,
    contentRadius = 40,
    bezelWidth = 15,
    showNotch = false,
    isModal = false,
    sticky = false,
    skipBorder = false,
    iFrameRenderKey = "static-render-key",
    setPaywallSubstitutions,
    setSubstitutionsFromPing,
    subs,
    viewFreeTrial,
    variables,
    enableHoverAndClick,
    pageStyles,
    onChangePageStyles = () => {},
    setTemplateJson = (val: TPaywallTemplate | null) => {},
    paywall,
    hasError,
    setIframeRef = (ref: RefObject<HTMLIFrameElement | null>) => {},
    isSubstitutionFocused = false,
    selectedSubstitutionKey,
    setSelectedSubstitutionKey,
    hoverSubstitutionKey,
    isLoading = false,
    mode,
  } = props

  const { pageStyles: pageStylesFromDB, v4 } = paywall || {}

  const { iFrameSrc, iFrameRef, initializeIFrame, hasFailed } = usePaywallPreviewLifecycle({
    previewUrl,
    iFrameRenderKey,
    setPaywallSubs: setPaywallSubstitutions,
    setSubstitutionsFromPing,
    subs,
    viewFreeTrial,
    variables,
    enableHoverAndClick,
    pageStyles: pageStyles || [],
    updatePageStyles: onChangePageStyles,
    pageStylesFromDB: pageStylesFromDB as TPageStyles,
    setTemplateJson: setTemplateJson,
    setIframeRef,
    isSubstitutionFocused,
    selectedSubstitutionKey,
    setSelectedSubstitutionKey,
    hoverSubstitutionKey,
  })

  // Override so we don't need a ping message (which is not present in the preview)
  const isErrorState = v4 ? false : hasError || hasFailed

  const contentHeight = contentWidth * aspectRatio
  const showModal = isModal && showNotch
  const modalCornerRadius = 15
  const modalOffset = (170 / 2532) * contentHeight

  const deviceFrameProps = useMemo(
    () => ({
      backgroundColor,
      deviceColor,
      scale,
      maxWidth,
      contentWidth,
      aspectRatio,
      contentRadius,
      bezelWidth,
      showNotch,
      isModal,
      sticky,
      skipBorder,
      hasError: isErrorState,
      orientation,
    }),
    [
      backgroundColor,
      deviceColor,
      scale,
      maxWidth,
      contentWidth,
      aspectRatio,
      contentRadius,
      bezelWidth,
      showNotch,
      isModal,
      sticky,
      skipBorder,
      isErrorState,
      orientation,
    ]
  )

  const parsedSrc = new URL(iFrameSrc)
  if (mode) {
    parsedSrc.searchParams.append("mode", mode)
  }

  if (mode === "editorV3") {
    return (
      <Container>
        <SFlex margin="mt-4 mb-[-18px]" justify="center" align="center" gap="2">
          <OrientationIconContainer onClick={() => setOrientation("portrait")}>
            <Icon
              name="MdStayCurrentPortrait"
              size="xl"
              color={orientation === "portrait" ? "accentLight" : "whiteOpacity20"}
            />
          </OrientationIconContainer>
          <span className="text-white text-opacity-60 text-xs">{" | "}</span>
          <OrientationIconContainer onClick={() => setOrientation("landscape")}>
            <Icon
              name="MdStayCurrentLandscape"
              size="xl"
              color={orientation === "landscape" ? "accentLight" : "whiteOpacity20"}
            />
          </OrientationIconContainer>
        </SFlex>

        <IOS {...deviceFrameProps}>
          <iframe
            name={`paywall-preview-iframe`}
            className={`paywall-preview-iframe`}
            key={iFrameSrc}
            ref={iFrameRef}
            src={mode ? parsedSrc.toString() : iFrameSrc}
            style={{
              overscrollBehavior: "contain",
              width: "100%",
              transition: "* 0.4s ease",
              ...(showModal
                ? {
                    height: `calc(100% - ${modalOffset}px)`,
                    borderTopRightRadius: `${modalCornerRadius}px`,
                    borderTopLeftRadius: `${modalCornerRadius}px`,
                  }
                : {
                    height: `100%`,
                  }),
            }}
            onLoad={initializeIFrame}
          />
          <LoaderContainer $isHidden={!isLoading}>
            <SOverlaySpinner size="lg" overlayColor="#000000" />
          </LoaderContainer>
        </IOS>
      </Container>
    )
  } else {
    return (
      <IOS {...deviceFrameProps}>
        <iframe
          name={`paywall-preview-iframe`}
          className={`paywall-preview-iframe`}
          key={iFrameSrc}
          ref={iFrameRef}
          src={mode ? parsedSrc.toString() : iFrameSrc}
          style={{
            overscrollBehavior: "contain",
            width: "100%",
            transition: "* 0.4s ease",
            ...(showModal
              ? {
                  height: `calc(100% - ${modalOffset}px)`,
                  borderTopRightRadius: `${modalCornerRadius}px`,
                  borderTopLeftRadius: `${modalCornerRadius}px`,
                }
              : {
                  height: `100%`,
                }),
          }}
          onLoad={initializeIFrame}
        />
        <LoaderContainer $isHidden={!isLoading}>
          <SOverlaySpinner size="lg" overlayColor="#000000" />
        </LoaderContainer>
      </IOS>
    )
  }
}

export default PaywallPreview
